import { useCallback } from 'react';
import { CONTEXT_TYPES, ORDER_TYPES } from '@nandosaus/constants';
import { isNaN, isNil, parseInt, split } from 'lodash';
import { useRouter } from 'next/router';
import { useRootStore } from '@nandosaus/state-management';
import moment from 'moment';

import { useFeature } from '@hooks/use-feature';

const validateNumberQueryParam = queryParam => !isNil(queryParam) && !isNaN(parseInt(queryParam));

// Helper function to get first available catering date for a restaurant
const getFirstAvailableCateringDate = restaurant => {
  if (!restaurant?.cateringWindow) return null;

  const today = moment().startOf('day');
  const todayDayIndex = today.day();
  const config = restaurant.cateringWindow.find(cfg => cfg.dayIndex === todayDayIndex);

  if (!config) return null;

  // Check up to 30 days from today to find the first available date
  for (let daysFromToday = config.cateringPickupWindowStartsInDays; daysFromToday < 30; daysFromToday += 1) {
    const checkDate = moment(today).add(daysFromToday, 'days');
    const daysFromTodayCheck = checkDate.diff(today, 'days');

    if (
      daysFromTodayCheck >= config.cateringPickupWindowStartsInDays &&
      daysFromTodayCheck <= config.cateringPickupWindowStartsInDays + config.cateringPickupWindowDurationDays
    ) {
      return checkDate.format('YYYY-MM-DD');
    }
  }
  return null;
};

// Helper function to get first available catering time for a restaurant
const getFirstAvailableCateringTime = restaurant => {
  if (!restaurant) return null;

  const availablePickupTimes = restaurant.availablePickupTimes({ orderType: ORDER_TYPES.CATERING });
  return availablePickupTimes.length > 0 ? availablePickupTimes[0].value : null;
};

const getCateringQueryParams = (query = {}) => {
  let queryRestaurant = query.r;

  const [jointRestaurant, jointOrderType] = split(query.r || '', 'ot=');
  if (jointRestaurant && jointOrderType) {
    queryRestaurant = jointRestaurant;
  }

  const isValidRestaurant = validateNumberQueryParam(queryRestaurant);

  const showAllOrderTypes = !isNil(query.showAllOrderTypes) && query.showAllOrderTypes === 'true';

  return {
    restaurant: isValidRestaurant ? queryRestaurant : undefined,
    orderType: ORDER_TYPES.CATERING,
    showAllOrderTypes,
  };
};

export const useCateringPreSet = () => {
  const router = useRouter();
  const { AlertsStore, OrderingContextStore, RestaurantStore } = useRootStore();
  const cateringDeliveryEnabled = useFeature('catering-delivery-enabled');

  const params = getCateringQueryParams(router?.query);

  const isRestaurantPreSet = params.restaurant !== undefined;

  const applyPreSet = useCallback(async () => {
    const applyCateringPrefillOptions = () => {
      const restaurant = RestaurantStore.getRestaurantById(params.restaurant);

      // Get default values if not provided in query params
      const defaultCateringPickupDate = getFirstAvailableCateringDate(restaurant);
      const defaultCateringPickupTime = getFirstAvailableCateringTime(restaurant);
      const defaultCateringHeadcount = 0;

      OrderingContextStore.switchContext(CONTEXT_TYPES.PRESET, {
        orderType: ORDER_TYPES.CATERING, // Always force catering order type as the default selection
        restaurantId: restaurant?.id,
        cateringPickupDate: defaultCateringPickupDate,
        cateringPickupTime: defaultCateringPickupTime,
        cateringHeadcount: defaultCateringHeadcount,
        // Also true when catering-delivery-enabled, so CATERING_DELIVERY is included in
        // availableOrderTypes and the "Delivery" tile in the order-type picker is selectable
        // without needing the showAllOrderTypes query param.
        showAllOrderTypes: params.showAllOrderTypes || cateringDeliveryEnabled,
      }).whenFail((_, result) => AlertsStore.add(result.toAlert()));

      return {
        hasValidOrderSettings: OrderingContextStore.isValid,
      };
    };

    if (isRestaurantPreSet) {
      await (RestaurantStore.loaded ? null : RestaurantStore.loadRestaurants());

      return applyCateringPrefillOptions();
    }

    // Reset context and ensure catering order type is set
    OrderingContextStore.resetContext({ only: CONTEXT_TYPES.PRESET });

    return {
      hasValidOrderSettings: true,
    };
    // cateringDeliveryEnabled is a real dep: LaunchDarkly flags can resolve after this
    // callback is first created, and without it here the memoized closure (and the
    // mount effect that depends on this reference in the catering page) never re-runs
    // once the flag flips true, leaving availableOrderTypes stuck without CATERING_DELIVERY.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRestaurantPreSet, cateringDeliveryEnabled]);

  const hasAllSettings = OrderingContextStore.isValid;

  return { isRestaurantPreSet, hasAllSettings, applyPreSet };
};
